import React, { useState, useRef, useEffect } from 'react';
import { supabase } from './supabaseClient';
import SignatureCanvas from 'react-signature-canvas';
import { motion } from "motion/react";



const APP_VERSION = "1.0.4 - FIX_FIRMA_LOGS";

const SupasoLogoAnimated = ({ size = "normal" }) => {
    const isSmall = size === "small";
    const scale = isSmall ? 0.4 : 1;
    return (
        <div className={`relative flex items-center justify-center ${isSmall ? '' : 'mb-6'}`}>
            <div className="relative flex items-center justify-center" style={{ transform: `scale(${scale})` }}>
                <motion.div
                    className="absolute w-[103px] h-[103px] border-[7px] border-transparent border-t-[#00A400] border-r-[#00A400] border-b-[#00A400] rounded-full"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                    className="absolute w-[86px] h-[86px] border-[7px] border-transparent border-t-[#FD3033] border-l-[#FD3033] border-b-[#FD3033] rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                />
                <div className="relative z-10 flex items-center justify-center bg-white rounded-full p-1 shadow-sm">
                    <img src="https://supaso.org/logo.png" alt="Logo" className="w-[67px] h-[67px] object-contain" referrerPolicy="no-referrer" />
                </div>
            </div>
        </div>
    );
};

// --- UTILIDADES ---

const validateCUIT = (cuit) => {
    const cleanCUIT = cuit.replace(/[^\d]/g, '');
    if (cleanCUIT.length !== 11) return false;
    const factors = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cleanCUIT[i]) * factors[i];
    let checkDigit = 11 - (sum % 11);
    if (checkDigit === 11) checkDigit = 0;
    if (checkDigit === 10) checkDigit = 9;
    return checkDigit === parseInt(cleanCUIT[10]);
};

const formatDNI = (value) => {
    const clean = value.replace(/[^\d]/g, '');
    return clean.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const processImage = async (file, maxWidth = 1200, log) => {
    log(`Procesando imagen: ${file.name}...`);
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                if (width > maxWidth) {
                    height = (maxWidth * height) / width;
                    width = maxWidth;
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    log(`Imagen ${file.name} lista (${(blob.size / 1024).toFixed(1)} KB)`);
                    resolve(blob);
                }, 'image/webp', 0.82);
            };
        };
    });
};

const PROVINCIAS = [
    "Buenos Aires", "CABA", "Catamarca", "Chaco", "Chubut", "Córdoba", "Corrientes",
    "Entre Ríos", "Formosa", "Jujuy", "La Pampa", "La Rioja", "Mendoza", "Misiones",
    "Neuquén", "Río Negro", "Salta", "San Juan", "San Luis", "Santa Cruz", "Santa Fe",
    "Santiago del Estero", "Tierra del Fuego", "Tucumán"
];

export default function AfiliadosSolicitud({ onBack }) {
    const [loading, setLoading] = useState(false);
    const [tipoAfiliacion, setTipoAfiliacion] = useState('ACTIVO');
    const [dni, setDni] = useState('');
    const [telefono, setTelefono] = useState('');
    const [telEmpresa, setTelEmpresa] = useState('');
    const [errors, setErrors] = useState({});
    const [logs, setLogs] = useState([]);
    const sigPad = useRef(null);
    const [botField, setBotField] = useState('');

    const addLog = (msg) => {
        console.log(`[ALTA]: ${msg}`);
        setLogs(prev => [...prev.slice(-9), `[${new Date().toLocaleTimeString()}] ${msg}`]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (botField) return;

        setLoading(true);
        setErrors({});
        setLogs([]);
        addLog("Iniciando proceso de envío...");

        const fd = new FormData(e.target);
        const data = Object.fromEntries(fd.entries());

        // Validaciones
        addLog("Validando campos...");
        const newErrors = {};
        if (dni.replace(/[^\d]/g, '').length !== 8) newErrors.dni = "DNI debe tener 8 dígitos";
        if (telefono.length !== 10) newErrors.telefono = "Teléfono Personal: 10 dígitos obligatorios";

        if (data.fecha_nacimiento) {
            const birth = new Date(data.fecha_nacimiento);
            const now = new Date();
            let age = now.getFullYear() - birth.getFullYear();
            const m = now.getMonth() - birth.getMonth();
            if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
            if (age < 18) newErrors.fecha_nacimiento = "Debes ser mayor de 18 años.";
            if (age > 100) newErrors.fecha_nacimiento = "Edad no permitida (>100 años).";
        } else {
            newErrors.fecha_nacimiento = "Fecha de nacimiento obligatoria";
        }

        if (tipoAfiliacion === 'ACTIVO') {
            if (!validateCUIT(data.cuit_empleador)) newErrors.cuit = "CUIT Empleador inválido";
            if (telEmpresa.length !== 10) newErrors.tel_empresa = "Teléfono Empresa: 10 dígitos obligatorios";
        }

        if (sigPad.current && sigPad.current.isEmpty()) {
            newErrors.firma = "Firma digital obligatoria";
        }

        if (Object.keys(newErrors).length > 0) {
            addLog("⚠️ Errores de validación encontrados.");
            setErrors(newErrors);
            setLoading(false);
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            return;
        }

        try {
            addLog("Procesando documentos...");
            const uploadDoc = async (fileField, name) => {
                const file = fd.get(fileField);
                if (!file || file.size === 0) return null;
                const blob = await processImage(file, 1200, addLog);
                const path = `afiliados/${dni.replace(/[^\d]/g, '')}/${name}-${Date.now()}.webp`;
                addLog(`Subiendo ${name} a Supabase...`);
                const { error: upError } = await supabase.storage.from('afiliados-docs').upload(path, blob);
                if (upError) throw upError;
                return supabase.storage.from('afiliados-docs').getPublicUrl(path).data.publicUrl;
            };

            const urlFoto = await uploadDoc('foto_perfil', 'foto');
            const urlDniF = await uploadDoc('dni_frente', 'dni_f');
            const urlDniD = await uploadDoc('dni_dorso', 'dni_d');
            let urlRecibo = null;
            if (tipoAfiliacion === 'ACTIVO') {
                urlRecibo = await uploadDoc('recibo_sueldo', 'recibo');
            }

            addLog("Capturando firma...");
            // EXTRA SAFE FIRMA ACCESS
            if (!sigPad.current) throw new Error("Referencia a firma no válida");

            let canvas;
            try {
                canvas = sigPad.current.getTrimmedCanvas();
            } catch (fError) {
                addLog("Error crítico accediendo a getTrimmedCanvas. Intentando fallback...");
                canvas = sigPad.current.getCanvas();
            }

            if (!canvas) throw new Error("No se pudo obtener el canvas de la firma");

            const firmaBlob = await new Promise(res => {
                canvas.toBlob(res, 'image/webp', 0.8);
            });
            const firmaPath = `afiliados/${dni.replace(/[^\d]/g, '')}/firma-${Date.now()}.webp`;
            addLog("Subiendo firma...");
            await supabase.storage.from('afiliados-docs').upload(firmaPath, firmaBlob);
            const urlFirma = supabase.storage.from('afiliados-docs').getPublicUrl(firmaPath).data.publicUrl;

            addLog("Guardando datos en la base de datos...");
            const { error: dbError } = await supabase.from('afiliados').insert([{
                tipo_afiliacion: tipoAfiliacion,
                nombre: data.nombre.toUpperCase(),
                apellido: data.apellido.toUpperCase(),
                dni: dni.replace(/[^\d]/g, ''),
                correo: data.email.toLowerCase(),
                telefono: telefono,
                fecha_nacimiento: data.fecha_nacimiento,
                provincia: data.provincia,
                departamento: data.departamento.toUpperCase(),
                ciudad: data.ciudad.toUpperCase(),
                direccion: data.direccion.toUpperCase(),
                razon_social: data.razon_social?.toUpperCase() || null,
                cuit_empleador: data.cuit_empleador || null,
                telefono_empresa: telEmpresa || null,
                sueldo_bruto: parseFloat(data.sueldo_bruto) || null,
                url_foto_perfil: urlFoto,
                url_dni_frente: urlDniF,
                url_dni_dorso: urlDniD,
                url_recibo_sueldo: urlRecibo,
                url_firma: urlFirma,
                estado: 'pendiente'
            }]);

            if (dbError) throw dbError;

            addLog("✅ EXITO: Registro completado.");
            alert("¡Solicitud enviada con éxito!");
            window.location.reload();

        } catch (err) {
            addLog(`❌ ERROR: ${err.message}`);
            console.error(err);
            alert(`Error crítico: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 font-sans pb-40">
            <div className="max-w-4xl mx-auto space-y-8">

                <div className="text-center space-y-2 relative">
                    <SupasoLogoAnimated />
                    <span className="absolute -top-4 right-0 text-[8px] font-bold text-slate-300">v{APP_VERSION}</span>

                    {onBack && (
                        <button
                            onClick={onBack}
                            className="absolute -top-2 left-0 flex items-center gap-1 text-slate-400 hover:text-blue-600 transition-colors font-bold text-xs uppercase"
                        >
                            <span className="material-icons text-sm">arrow_back</span>
                            Volver
                        </button>
                    )}

                    <h1 className="text-4xl font-black text-blue-900 tracking-tight uppercase">Solicitud de Afiliación</h1>
                    <p className="text-slate-500 font-medium text-xs sm:text-sm">Sindicato Unico de Profesionales y Afines de la Seguridad Ocupacional y Medio Ambiente</p>
                </div>

                {/* LOGS PANEL SI HAY ACTIVIDAD */}
                {logs.length > 0 && (
                    <div className="bg-slate-900 border-2 border-slate-800 p-4 rounded-3xl shadow-2xl font-mono text-[10px] text-green-400 overflow-hidden">
                        <div className="flex items-center gap-2 mb-2 border-b border-slate-800 pb-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            <span className="font-bold uppercase tracking-widest text-slate-400">Estado del Proceso (Logs)</span>
                        </div>
                        <div className="space-y-1">
                            {logs.map((l, i) => <div key={i} className="animate-in slide-in-from-left duration-200">{l}</div>)}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-10">
                    <input type="text" value={botField} onChange={e => setBotField(e.target.value)} className="hidden" tabIndex="-1" />

                    {/* SECCIÓN 1: TIPO */}
                    <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 ring-1 ring-slate-900/5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-black">01</span>
                            <h2 className="text-xl font-bold text-slate-800 uppercase tracking-wide">Tipo de Afiliación</h2>
                        </div>
                        <select
                            name="tipo_afiliacion"
                            value={tipoAfiliacion}
                            onChange={e => setTipoAfiliacion(e.target.value)}
                            className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700"
                        >
                            <option value="ACTIVO">ACTIVO (En actividad laboral)</option>
                            <option value="ADHERENTE">ADHERENTE</option>
                            <option value="JUBILADO">JUBILADO</option>
                            <option value="HONORARIO">HONORARIO</option>
                        </select>
                    </section>

                    {/* SECCIÓN 2: PERSONALES */}
                    <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 ring-1 ring-slate-900/5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-black">02</span>
                            <h2 className="text-xl font-bold text-slate-800 uppercase tracking-wide">Datos Personales</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">DNI (8 dígitos)</label>
                                <input type="text" value={dni} onChange={e => setDni(formatDNI(e.target.value))} maxLength={11} placeholder="00.000.000" required className={`w-full p-4 bg-slate-50 border-2 rounded-2xl outline-none transition-all font-bold text-slate-700 ${errors.dni ? 'border-red-500' : 'border-slate-100 focus:border-blue-500 focus:bg-white'}`} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Fecha de Nacimiento</label>
                                <input name="fecha_nacimiento" type="date" required className={`w-full p-4 bg-slate-50 border-2 rounded-2xl outline-none transition-all font-bold text-slate-700 ${errors.fecha_nacimiento ? 'border-red-500' : 'border-slate-100 focus:border-blue-500 focus:bg-white'}`} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Nombre</label>
                                <input name="nombre" type="text" required placeholder="NOMBRES" className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Apellido</label>
                                <input name="apellido" type="text" required placeholder="APELLIDOS" className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Email Personal</label>
                                <input name="email" type="email" required placeholder="correo@ejemplo.com" className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">WhatsApp Personal (10 dígitos)</label>
                                <input type="text" value={telefono} onChange={e => setTelefono(e.target.value.replace(/[^\d]/g, '').slice(0, 10))} placeholder="1122334455" required className={`w-full p-4 bg-slate-50 border-2 rounded-2xl outline-none transition-all font-bold text-slate-700 ${errors.telefono ? 'border-red-500' : 'border-slate-100 focus:border-blue-500 focus:bg-white'}`} />
                            </div>
                        </div>
                    </section>

                    {/* SECCIÓN 3: DOMICILIO */}
                    <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 ring-1 ring-slate-900/5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-black">03</span>
                            <h2 className="text-xl font-bold text-slate-800 uppercase tracking-wide">Domicilio</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1 col-span-2 md:col-span-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Provincia</label>
                                <select name="provincia" required className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700">
                                    {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Departamento / Partido</label>
                                <input name="departamento" type="text" required placeholder="Partido" className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Ciudad / Barrio</label>
                                <input name="ciudad" type="text" required placeholder="Localidad" className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Dirección (Calle y Nro)</label>
                                <input name="direccion" type="text" required placeholder="Calle 123" className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700" />
                            </div>
                        </div>
                    </section>

                    {/* SECCIÓN 4: LABORALES */}
                    {tipoAfiliacion === 'ACTIVO' && (
                        <section className="bg-blue-50 p-8 rounded-3xl shadow-sm border border-blue-100 ring-1 ring-blue-900/5 transition-all animate-in slide-in-from-top-4 duration-500">
                            <div className="flex items-center gap-4 mb-6">
                                <span className="w-10 h-10 rounded-2xl bg-blue-800 text-white flex items-center justify-center font-black">04</span>
                                <h2 className="text-xl font-bold text-blue-900 uppercase tracking-wide">Datos Laborales</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-blue-400 uppercase">Razón Social Empleador</label>
                                    <input name="razon_social" type="text" required placeholder="Empresa" className="w-full p-4 bg-white border-2 border-blue-100 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold text-slate-700" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-blue-400 uppercase">CUIT Empleador</label>
                                    <input name="cuit_empleador" type="text" placeholder="30123456789" required className={`w-full p-4 bg-white border-2 rounded-2xl outline-none transition-all font-bold text-slate-700 ${errors.cuit ? 'border-red-500' : 'border-blue-100 focus:border-blue-500'}`} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-blue-400 uppercase">Teléfono Empresa (10 dígitos)</label>
                                    <input type="text" value={telEmpresa} onChange={e => setTelEmpresa(e.target.value.replace(/[^\d]/g, '').slice(0, 10))} placeholder="1122334455" required className={`w-full p-4 bg-white border-2 rounded-2xl outline-none transition-all font-bold text-slate-700 ${errors.tel_empresa ? 'border-red-500' : 'border-blue-100 focus:border-blue-500'}`} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-blue-400 uppercase">Sueldo Bruto</label>
                                    <input name="sueldo_bruto" type="number" step="0.01" required placeholder="0.00" className="w-full p-4 bg-white border-2 border-blue-100 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold text-slate-700" />
                                </div>
                            </div>
                        </section>
                    )}

                    {/* SECCIÓN 5: DOCUMENTACIÓN */}
                    <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 ring-1 ring-slate-900/5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-black">05</span>
                            <h2 className="text-xl font-bold text-slate-800 uppercase tracking-wide">Documentación y Firma</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Foto Perfil (Cara)</label>
                                <input name="foto_perfil" type="file" accept="image/*" required className="w-full text-xs text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-2xl file:border-0 file:bg-blue-50 file:text-blue-700 cursor-pointer" />
                            </div>
                            <div className="space-y-2 bg-slate-50 p-4 rounded-3xl border-2 border-dashed border-slate-200">
                                <label className="text-[10px] font-black text-slate-400 uppercase block text-center mb-2">Firma Digital</label>
                                <div className="bg-white rounded-xl border border-slate-300 overflow-hidden">
                                    <SignatureCanvas ref={sigPad} penColor='black' canvasProps={{ className: "w-full h-40" }} />
                                </div>
                                <button type="button" onClick={() => sigPad.current.clear()} className="w-full text-[8px] font-black text-red-500 uppercase mt-2">Limpiar Firma</button>
                            </div>
                            <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase">DNI Frente</label><input name="dni_frente" type="file" accept="image/*" required className="w-full text-xs text-slate-500 file:rounded-2xl file:border-0 file:bg-blue-50 file:text-blue-700 cursor-pointer" /></div>
                            <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase">DNI Dorso</label><input name="dni_dorso" type="file" accept="image/*" required className="w-full text-xs text-slate-500 file:rounded-2xl file:border-0 file:bg-blue-50 file:text-blue-700 cursor-pointer" /></div>
                            {tipoAfiliacion === 'ACTIVO' && (
                                <div className="space-y-2 col-span-2 md:col-span-1"><label className="text-[10px] font-black text-blue-500 uppercase">Recibo de Sueldo</label><input name="recibo_sueldo" type="file" accept="image/*" required className="w-full text-xs text-slate-500 file:rounded-2xl file:border-0 file:bg-blue-50 file:text-blue-700 cursor-pointer" /></div>
                            )}
                        </div>
                    </section>

                    <section className="bg-slate-900 border-l-8 border-blue-500 p-8 rounded-3xl shadow-xl">
                        <div className="flex items-start gap-4">
                            <input type="checkbox" required className="w-6 h-6 rounded-lg mt-1" />
                            <p className="text-slate-300 text-sm leading-relaxed"><span className="text-white font-black block mb-2 text-[10px] tracking-widest uppercase">DECLARACIÓN JURADA:</span>Declaro bajo juramento que la información proporcionada es veraz, exacta y completa, y que ha sido consignada de buena fe. Asumo plena responsabilidad legal por la autenticidad de los datos aportados y me comprometo a notificar por escrito cualquier modificación que pudiera afectar a su validez en un plazo máximo de 30 días corridos desde que se produzca.</p>
                        </div>
                    </section>

                    <button type="submit" disabled={loading} className={`w-full py-6 rounded-3xl text-white font-black text-xl tracking-widest shadow-2xl transition-all flex items-center justify-center gap-4 ${loading ? 'bg-slate-400 animate-pulse' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/20 active:scale-95'}`}>
                        {loading ? <SupasoLogoAnimated size="small" /> : null}
                        {loading ? "PROCESANDO..." : "ENVIAR SOLICITUD"}
                    </button>
                </form>
            </div>

            {/* PANEL DE ERRORES AL PIE */}
            {Object.keys(errors).length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 bg-red-600 text-white p-6 z-[60] shadow-[0_-10px_50px_rgba(0,0,0,0.3)] border-t-4 border-white animate-in slide-in-from-bottom-full duration-300 font-bold">
                    <div className="max-w-4xl mx-auto flex items-center justify-between">
                        <div>
                            <p className="uppercase tracking-widest text-xs font-black">Revisa los siguientes campos:</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {Object.values(errors).map((m, i) => <span key={i} className="text-[10px] bg-black/20 px-2 py-0.5 rounded">{m}</span>)}
                            </div>
                        </div>
                        <button onClick={() => setErrors({})} className="bg-white text-red-600 px-4 py-2 rounded-xl text-[10px] uppercase font-black">X</button>
                    </div>
                </div>
            )}

            {/* Footer corporativo interno */}
            <div className="w-full py-8 text-center bg-slate-950">
                <p style={{ fontSize: '9px', color: '#00A400', fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em' }} className="font-medium">
                    © {new Date().getFullYear()} - <span style={{ fontFamily: '"Russo One", sans-serif', fontWeight: 500, color: '#0086CE' }}>SUPASO</span> <span style={{ color: '#0086CE', fontWeight: 700 }}>Tecnología</span> - v3.5
                </p>
            </div>

            <style jsx>{` @import url('https://fonts.googleapis.com/icon?family=Material+Icons'); `}</style>
        </div>
    );
}
